import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  FormControl,
  alpha,
  useTheme,
  InputAdornment,
  Pagination,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Calculate as CalculateIcon,
  Category as CategoryIcon,
  Sync as SyncIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Public as PublicIcon,
  Language as LanguageIcon
} from '@mui/icons-material';

import TikTokIcon from '@mui/icons-material/MusicNote'; // Fallback para TikTok
import api from '../../../../services/api';
import { useBrand } from '../../../../context/BrandContext';
import {
  operationalComponentSurfaces,
  operationalElevation,
  operationalFeedback,
  operationalFontSizes,
  operationalMotion,
  operationalRadii,
  operationalSizing,
  operationalStatusColors
} from '../../../../design-system/tokens';

const TIER_LABELS = {
  1: 'T1',
  2: 'T2',
  3: 'T3'
};

const normalizeCategory = (category) => category
  ? String(category).toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '')
      .replace(/s$/, '')
  : '';

const normalizeSourceSearch = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  try {
    const shouldParseAsUrl = raw.includes('://') || (raw.includes('.') && raw.includes('/'));
    if (!shouldParseAsUrl) return raw;

    const url = new URL(raw.includes('://') ? raw : `https://${raw}`);
    return url.hostname.replace(/^www\./, '');
  } catch (error) {
    return raw.replace(/^https?:\/\//, '').split('/')[0].replace(/^www\./, '');
  }
};

const CategorySelect = ({ source, allCategories, onChange, currentValues, brandRelevantCategories = [] }) => {
  const theme = useTheme();
  const [localValues, setLocalValues] = useState(currentValues || []);
  const chipTokens = operationalComponentSurfaces.chip[theme.palette.mode === 'dark' ? 'dark' : 'light'];

  useEffect(() => {
    setLocalValues(currentValues || []);
  }, [currentValues]);

  const handleChange = (event, newValue) => {
    setLocalValues(newValue);
    onChange(newValue);
  };

  // Categorias "sugeridas" (estão na fonte mas não selecionadas ainda)
  const sourceCats = source.globalCategories || (source.globalCategory ? [source.globalCategory] : []);
  const suggestions = sourceCats.filter(cat => !localValues.includes(cat));

  return (
    <Box sx={{ minWidth: 250, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Autocomplete
        multiple
        size="small"
        options={allCategories}
        value={localValues}
        onChange={handleChange}
        disableCloseOnSelect
        getOptionLabel={(option) => option}
        isOptionEqualToValue={(option, value) => option === value}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={localValues.length === 0 ? "Categorias..." : ""}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: operationalFontSizes.xs,
                minHeight: operationalSizing.compact,
                p: '2px !important',
                borderRadius: `${operationalRadii.control}px`,
                bgcolor: 'background.paper',
              },
              '& fieldset': { borderColor: 'divider' }
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.filter(option => option && option !== 'undefined').map((option, index) => {
            const isRelevant = brandRelevantCategories.includes(option);
            return (
              <Chip
                label={option}
                size="small"
                {...getTagProps({ index })}
                sx={{ 
                  height: 20, 
                  fontSize: '0.65rem',
                  bgcolor: isRelevant ? 'primary.main' : chipTokens.background,
                  color: isRelevant ? theme.palette.getContrastText(theme.palette.primary.main) : chipTokens.textHover,
                  fontWeight: isRelevant ? 700 : 600,
                  '& .MuiChip-deleteIcon': {
                    color: isRelevant
                      ? `${theme.palette.getContrastText(theme.palette.primary.main)} !important`
                      : `${chipTokens.text} !important`,
                    '&:hover': {
                      color: `${chipTokens.textHover} !important`
                    }
                  }
                }}
              />
            );
          })
        }
        sx={{ width: '100%' }}
      />
      {suggestions.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled', mr: 0.5 }}>
            Sugeridas:
          </Typography>
          {suggestions.map(cat => (
            <Chip
              key={cat}
              label={cat}
              size="small"
              variant="outlined"
              onClick={() => handleChange(null, [...localValues, cat])}
              sx={{ 
                height: 16, 
                fontSize: '0.6rem', 
                cursor: 'pointer',
                opacity: 0.82,
                '&:hover': { opacity: 1, bgcolor: chipTokens.backgroundHover }
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const BrandTierManager = () => {
  const theme = useTheme();
  const { activeBrandData } = useBrand();
  const surfaceSx = {
    borderRadius: `${operationalRadii.surface}px`,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: operationalElevation.none
  };
  const alertSx = (severity = 'info') => ({
    borderRadius: `${operationalRadii.control}px`,
    border: '1px solid',
    borderLeft: `${operationalFeedback.alert.accentBorderWidth}px solid ${operationalStatusColors[severity] || operationalStatusColors.info}`,
    borderColor: 'divider',
    bgcolor: 'background.paper',
    color: 'text.primary',
    boxShadow: operationalElevation.none,
    '& .MuiAlert-icon': {
      color: operationalStatusColors[severity] || operationalStatusColors.info
    }
  });
  const tierAccent = {
    1: operationalStatusColors.success,
    2: operationalStatusColors.warning,
    3: theme.palette.text.secondary
  };

  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState([]);
  const [thresholds, setThresholds] = useState(null);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0,
    hasMore: false
  });

  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch thresholds and categories once
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [threshRes, catRes] = await Promise.all([
          api.get('/tiers/thresholds'),
          api.get('/tiers/categories')
        ]);
        setThresholds(threshRes.data.data);
        setCategories((catRes.data.data || []).map(c => typeof c === 'object' ? c.value : c));
      } catch (err) {
        console.error('Erro ao buscar metadados:', err);
      }
    };
    fetchMeta();
  }, []);

  const fetchSources = useCallback(async (page = 1) => {
    if (!activeBrandData?._id) return;
    setLoading(true);
    try {
      const response = await api.get('/tiers/sources', {
        params: {
          page,
          limit: pagination.limit,
          search: normalizeSourceSearch(debouncedSearch)
        }
      });
      setSources(response.data.data);
      setPagination(prev => ({
        ...prev,
        page,
        total: response.data.pagination.total,
        hasMore: response.data.pagination.hasMore
      }));
    } catch (err) {
      setError('Erro ao carregar fontes');
    } finally {
      setLoading(false);
    }
  }, [activeBrandData?._id, debouncedSearch, pagination.limit]);

  useEffect(() => {
    fetchSources(pagination.page);
  }, [fetchSources, pagination.page]);

  // Helpers de lógica de Tier (replicados do Admin para consistência visual)
  const getPlatformGroup = useCallback((platform, matrix) => {
    if (!platform || !matrix) return 'online_portal';
    const normalizedPlatform = platform.toLowerCase();
    for (const groupKey in matrix) {
        const group = matrix[groupKey];
        if (group && group.platforms && group.platforms.some(p => p.toLowerCase() === normalizedPlatform)) {
            return groupKey;
        }
    }
    if (['instagram', 'facebook', 'tiktok', 'youtube'].includes(normalizedPlatform)) return 'social_mass';
    if (['twitter', 'x', 'threads', 'linkedin'].includes(normalizedPlatform)) return 'social_info';
    return 'online_portal';
  }, []);

  const calculateAutoTier = useCallback((metricValue, platform) => {
    if (!thresholds || !activeBrandData) return 3;
    const groupKey = getPlatformGroup(platform, thresholds);
    const group = thresholds[groupKey] || thresholds['online_portal'];
    if (!group || !group.thresholds) return 3;

    const profile = activeBrandData.tierProfile || 'medium';
    const profileThresholds = group.thresholds[profile] || group.thresholds['medium'] || 10000;
    
    let t1, t2;
    if (typeof profileThresholds === 'object') {
      t1 = profileThresholds.t1 || 10000;
      t2 = profileThresholds.t2 || Math.round(t1 * 0.1);
    } else {
      t1 = Number(profileThresholds);
      t2 = Math.round(t1 * 0.1);
    }

    if (metricValue >= t1) return 1;
    if (metricValue >= t2) return 2;
    return 3;
  }, [thresholds, activeBrandData, getPlatformGroup]);

  const hasCategoryBoost = useCallback((category) => {
    if (!category || !activeBrandData?.relevantCategories) return false;
    const normalizedCategory = normalizeCategory(category);
    return activeBrandData.relevantCategories.some((brandCategory) => {
      const normalizedBrandCategory = normalizeCategory(brandCategory);
      if (!normalizedCategory || !normalizedBrandCategory) return false;
      return normalizedCategory === normalizedBrandCategory ||
        normalizedCategory.startsWith(normalizedBrandCategory.slice(0, 6)) ||
        normalizedBrandCategory.startsWith(normalizedCategory.slice(0, 6));
    });
  }, [activeBrandData]);

  const isLockedManualTierOverride = useCallback((source) => {
    if (!source.hasOverride || !source.override?.tier) return false;
    if (source.override.tierLocked === true) return true;
    if (source.override.tierLocked === false) return false;

    const overrideCategories = [
      ...(Array.isArray(source.override.categories) ? source.override.categories : []),
      ...(source.override.category ? [source.override.category] : [])
    ].filter(Boolean);

    return overrideCategories.length === 0;
  }, []);

  const getEffectiveTier = useCallback((source, overrideCategories = null) => {
    // Priorizar bônus de categoria (seja o atual ou o que está sendo definido)
    const brandRelevant = activeBrandData?.relevantCategories || [];
    const sourceCats = source.globalCategories || (source.globalCategory ? [source.globalCategory] : []);
    
    // Categorias atuais (considerando interseção se não houver override)
    const intersection = sourceCats.filter(cat => brandRelevant.includes(cat));
    const currentCats = source.hasOverride && (source.override?.categories?.length > 0 || source.override?.category)
      ? (source.override.categories?.length > 0 ? source.override.categories : [source.override.category])
      : (intersection.length > 0 ? intersection : sourceCats);
    
    const catsToTest = overrideCategories || currentCats;
    
    // 1. Priorizar override manual de Tier somente quando estiver travado.
    if (isLockedManualTierOverride(source)) return source.override.tier;
    
    // 2. Aplicar Bônus de Categoria Estratégica (Tier 1 Automático)
    if (catsToTest.some(c => hasCategoryBoost(c))) return 1;
    
    // 3. Cálculo Padrão (Audiência x Plataforma)
    return calculateAutoTier(source.maxSubscribers || 0, source.mediaType);
  }, [hasCategoryBoost, calculateAutoTier, activeBrandData, isLockedManualTierOverride]);

  // Handlers de Ação
  const handleQuickTierChange = async (source, newTier) => {
    try {
      const socialTypes = ['Instagram', 'TikTok', 'Twitter', 'YouTube', 'Facebook', 'LinkedIn', 'X', 'Threads'];
      const isSocial = socialTypes.includes(source.mediaType);
      const payload = {
        tier: newTier,
        tierLocked: true,
        reason: 'Ajuste manual do usuario',
        ...(isSocial ? { username: source.username, platform: source.mediaType.toLowerCase() } : { domain: source.domain })
      };

      if (source.hasOverride && source.override?._id) {
        await api.put(`/tiers/overrides/${source.override._id}`, {
          tier: newTier,
          tierLocked: true,
          reason: 'Ajuste manual do usuario'
        });
      } else {
        await api.post('/tiers/overrides', payload);
      }

      setSuccess('Tier atualizado!');
      await handleForceRecalculate({
        ...source,
        hasOverride: true,
        override: source.override?._id
          ? { ...source.override, tier: newTier, tierLocked: true }
          : null
      });
      fetchSources(pagination.page);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.existingId) {
        try {
          const existingId = err.response.data.existingId;
          await api.put(`/tiers/overrides/${existingId}`, {
            tier: newTier,
            tierLocked: true,
            reason: 'Ajuste manual do usuario'
          });
          setSuccess('Tier atualizado (override sincronizado)!');
          await handleForceRecalculate({
            ...source,
            hasOverride: true,
            override: {
              ...(source.override || {}),
              _id: existingId,
              tier: newTier,
              tierLocked: true
            }
          });
          fetchSources(pagination.page);
          setTimeout(() => setSuccess(''), 3000);
          return;
        } catch (putErr) {
          console.error('Erro ao tentar sincronizar override existente:', putErr);
        }
      }
      setError('Erro ao salvar alteração');
    }
  };

  const handleRemoveOverride = async (source) => {
    if (!source.override?._id) return;
    try {
      await api.delete(`/tiers/overrides/${source.override._id}`);
      setSuccess('Override removido');
      fetchSources(pagination.page);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao remover override');
    }
  };

  const handleForceRecalculate = async (source) => {
    try {
      setSuccess('Sincronizando artigos...');
      let res;
      if (source.override?._id) {
        // Tem override manual — usa endpoint específico (tier fixo)
        res = await api.post(`/tiers/overrides/${source.override._id}/recalculate`);
      } else {
        // Sem override — usa recalculo por domínio (reconhece categoria nova)
        const payload = source.username
          ? { username: source.username, platform: source.mediaType }
          : { domain: source.domain };
        res = await api.post('/tiers/sources/recalculate-by-domain', payload);
      }
      setSuccess(res.data?.message || 'Artigos sincronizados!');
      fetchSources(pagination.page);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError('Erro ao sincronizar artigos');
      setTimeout(() => setError(''), 5000);
    }
  };


  const handleCategoryChange = async (source, newCategories) => {
    try {
      const socialTypes = ['Instagram', 'TikTok', 'Twitter', 'YouTube', 'Facebook', 'LinkedIn', 'X', 'Threads'];
      const isSocial = socialTypes.includes(source.mediaType);
      const payload = {
        category: newCategories[0] || null,
        categories: newCategories,
        tier: getEffectiveTier(source, newCategories), // recalcular tier com as novas categorias
        tierLocked: false,
        ...(isSocial ? { username: source.username, platform: source.mediaType.toLowerCase() } : { domain: source.domain })
      };

      if (source.hasOverride && source.override?._id) {
        await api.put(`/tiers/overrides/${source.override._id}`, { 
          category: newCategories[0] || null, 
          categories: newCategories,
          tier: payload.tier,
          tierLocked: false
        });
      } else {
        await api.post('/tiers/overrides', payload);
      }

      setSuccess('Categorias atualizadas!');
      await handleForceRecalculate({
        ...source,
        hasOverride: true,
        override: source.override?._id
          ? { ...source.override, tier: payload.tier, tierLocked: false }
          : null
      });
      await fetchSources(pagination.page);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      // Se deu erro 400 porque já existe, tentar dar um PUT se tivermos o ID
      if (err.response?.status === 400 && err.response?.data?.existingId) {
        try {
          const existingId = err.response.data.existingId;
          await api.put(`/tiers/overrides/${existingId}`, { 
            category: newCategories[0] || null, 
            categories: newCategories,
            tier: getEffectiveTier(source, newCategories),
            tierLocked: false
          });
          setSuccess('Categorias atualizadas (override sincronizado)!');
          await handleForceRecalculate({
            ...source,
            hasOverride: true,
            override: {
              ...(source.override || {}),
              _id: existingId,
              tier: getEffectiveTier(source, newCategories),
              tierLocked: false
            }
          });
          await fetchSources(pagination.page);
          setTimeout(() => setSuccess(''), 3000);
          return;
        } catch (putErr) {
          console.error('Erro ao tentar sincronizar override existente:', putErr);
        }
      }
      
      setError(err.response?.data?.message || 'Erro ao atualizar categorias');
      setTimeout(() => setError(''), 5000);
    }
  };

  return (
    <Box>
      {/* Cabeçalho e Busca */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Ajuste Fino de Fontes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure exceções manuais e valide categorias para garantir o Tier 1 estratégico.
          </Typography>
        </Box>

        <TextField
          size="small"
          placeholder="Buscar fonte, dominio ou link..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: { xs: '100%', md: 300 },
            '& .MuiOutlinedInput-root': {
              minHeight: operationalSizing.default,
              borderRadius: `${operationalRadii.control}px`,
              bgcolor: 'background.paper'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Estatísticas Rápidas */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        {[
          { label: 'Tier 1 (Especialistas)', color: '#2e7d32', value: sources.filter(s => getEffectiveTier(s) === 1).length, total: sources.length },
          { label: 'Tier 2 (Relevantes)', color: '#ed6c02', value: sources.filter(s => getEffectiveTier(s) === 2).length, total: sources.length },
          { label: 'Tier 3 (Demais)', color: '#757575', value: sources.filter(s => getEffectiveTier(s) === 3).length, total: sources.length },
        ].map((stat, i) => (
          <Box key={i} sx={{ 
            ...surfaceSx,
            flex: 1, 
            minWidth: 200, 
            p: 2, 
            borderColor: alpha(stat.color, 0.34),
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}>
            <Typography variant="caption" fontWeight={700} color={stat.color} sx={{ textTransform: 'uppercase', letterSpacing: 0 }}>
              {stat.label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h5" fontWeight={800} color={stat.color}>
                {loading ? '...' : stat.value}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                fontes detectadas
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {success && <Alert severity="success" sx={{ ...alertSx('success'), mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ ...alertSx('error'), mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Fonte / Veículo</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Audiência</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Nicho / Categoria</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Tier Final</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from(new Array(5)).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ))
            ) : sources.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">Nenhuma fonte encontrada.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              sources.map((source) => {
                const effectiveTier = getEffectiveTier(source);
                const brandRelevant = activeBrandData?.relevantCategories || [];
                const sourceCats = source.globalCategories || (source.globalCategory ? [source.globalCategory] : []);
                
                // Lógica de pré-seleção inteligente:
                // 1. Se tem override manual, usa o override
                // 2. Se não tem override, tenta a interseção (Categorias da Fonte x Categorias da Marca)
                // 3. Se não tem interseção, mostra todas as da fonte (comportamento original)
                const intersection = sourceCats.filter(cat => brandRelevant.includes(cat));
                const cats = source.hasOverride && (source.override?.categories?.length > 0 || source.override?.category)
                  ? (source.override.categories?.length > 0 ? source.override.categories : [source.override.category])
                  : (intersection.length > 0 ? intersection : sourceCats);

                const hasBoost = cats.some(c => hasCategoryBoost(c));
                // isManual = tem override registrado (independente de ter boost de categoria)
                // usado para mostrar os botões de ação (sync + delete)
                const isManual = source.hasOverride && !!source.override?._id;

                return (
                  <TableRow key={source.domain || source.username} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ 
                          width: 36, 
                          height: 36, 
                          borderRadius: `${operationalRadii.control}px`,
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          bgcolor: 'action.selected',
                          color: (theme) => {
                            const type = source.mediaType?.toLowerCase();
                            if (type === 'youtube') return '#FF0000';
                            if (type === 'instagram') return '#E1306C';
                            if (type === 'facebook') return '#1877F2';
                            if (type === 'tiktok') return '#000000';
                            if (type === 'twitter' || type === 'x') return '#1DA1F2';
                            if (type === 'linkedin') return '#0077B5';
                            return theme.palette.primary.main;
                          }
                        }}>
                          {(() => {
                            const type = source.mediaType?.toLowerCase();
                            if (type === 'youtube') return <YouTubeIcon fontSize="small" />;
                            if (type === 'instagram') return <InstagramIcon fontSize="small" />;
                            if (type === 'facebook') return <FacebookIcon fontSize="small" />;
                            if (type === 'tiktok') return <TikTokIcon fontSize="small" />;
                            if (type === 'twitter' || type === 'x') return <TwitterIcon fontSize="small" />;
                            if (type === 'linkedin') return <LinkedInIcon fontSize="small" />;
                            if (type === 'online') return <LanguageIcon fontSize="small" />;
                            return <PublicIcon fontSize="small" />;
                          })()}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                            {source.siteName || (source.username ? `@${source.username}` : source.domain)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {source.username ? `@${source.username}` : (source.domain || source.mediaType)}
                            </Typography>
                            {source.username && source.domain && (
                              <Typography variant="caption" color="text.disabled">
                                • {source.domain}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={500}>
                        {source.maxSubscribers?.toLocaleString('pt-BR') || '0'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <CategorySelect
                        source={source}
                        allCategories={categories}
                        brandRelevantCategories={brandRelevant}
                        onChange={(val) => handleCategoryChange(source, val)}
                        currentValues={cats}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Select
                          value={effectiveTier}
                          onChange={(e) => handleQuickTierChange(source, Number(e.target.value))}
                          size="small"
                          sx={{
                            fontSize: '0.75rem',
                            height: 30,
                            width: 75,
                            fontWeight: 800,
                            bgcolor: tierAccent[effectiveTier],
                            color: 'white',
                            borderRadius: `${operationalRadii.control}px`,
                            '& .MuiSvgIcon-root': { color: 'white' },
                            '& fieldset': { border: 'none' }
                          }}
                        >
                          <MenuItem value={1}>T1</MenuItem>
                          <MenuItem value={2}>T2</MenuItem>
                          <MenuItem value={3}>T3</MenuItem>
                        </Select>

                        {hasBoost && (
                          <Tooltip title="Tier 1 garantido por Nicho estratégico">
                            <CalculateIcon sx={{ fontSize: 18, color: 'success.main' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title={source.override?._id
                          ? (isLockedManualTierOverride(source)
                            ? `Aplicar Tier ${source.override.tier} nos artigos desta fonte`
                            : 'Sincronizar artigos usando o match de categoria desta fonte')
                          : 'Sincronizar artigos com o tier atual desta fonte'
                        }>
                          <IconButton size="small" color="primary" onClick={() => handleForceRecalculate(source)}>
                            <SyncIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isManual && (
                          <Tooltip title="Remover ajuste manual">
                            <IconButton size="small" color="error" onClick={() => handleRemoveOverride(source)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Pagination 
          count={Math.ceil(pagination.total / pagination.limit)} 
          page={pagination.page} 
          onChange={(e, p) => setPagination(prev => ({ ...prev, page: p }))}
          color="primary"
          size="small"
        />
      </Box>
    </Box>
  );
};

export default BrandTierManager;
